import { format } from "date-fns";
import { Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface BundleExpirationBadgeProps {
  endDate: string;
  utilizedSessions: number;
  totalSessions: number;
}

export function BundleExpirationBadge({
  endDate,
  utilizedSessions,
  totalSessions,
}: BundleExpirationBadgeProps) {
  // Calculate days until expiration
  const calculateDaysUntilExpiration = (endDate: string): number => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilExpiration = calculateDaysUntilExpiration(endDate);
  const hasUnusedSessions = utilizedSessions < totalSessions;
  const showExpirationWarning = hasUnusedSessions && daysUntilExpiration >= 0;
  const isUrgent = daysUntilExpiration <= 7;

  if (!showExpirationWarning) {
    return null;
  }

  return (
    <Badge
      variant={isUrgent ? "destructive" : "default"}
      className={
        isUrgent
          ? "bg-red-100 text-red-800 border-red-300 hover:bg-red-100 hover:text-red-800 hover:border-red-300"
          : "bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-100 hover:text-gray-800 hover:border-gray-300"
      }
    >
      <span className="flex items-center gap-1">
        {daysUntilExpiration === 0
          ? "Bundle Expires Today"
          : `Bundle Expires in ${daysUntilExpiration} ${daysUntilExpiration === 1 ? "Day" : "Days"}`}
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3 w-3 cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Expires on {format(new Date(endDate), "PPP")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </span>
    </Badge>
  );
}

